import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { RankingType } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { Styles, css } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import { ImgOrLottie, contentfulImageUrl } from '../../../contentful/assets';
import { topRankLayout } from '../misc/rank';
import { getTopRankItemStyle } from '../misc/style';
import { competitionBunja, competitionEventsBunja, competitionTabsBunja } from '../state';

interface RankRewardProps {
  prizeList: { label: ReactNode; prize: string }[];
}

const TopRankList = ({ prizeList }: RankRewardProps) => {
  const { competitionContentful } = useBunja(competitionBunja);

  const { isMobile } = useBreakpoint();

  const getIconUrl = (rank: number) => {
    const url =
      rank === 1
        ? competitionContentful?.rank1Icon?.url
        : rank === 2
          ? competitionContentful?.rank2Icon?.url
          : competitionContentful?.rank3Icon?.url;
    return contentfulImageUrl(url ?? '');
  };

  return (
    <ul
      className={flex({
        direction: { base: 'column', tablet: 'row' },
        gap: { base: 0, tablet: 24 },
        justify: 'center',
      })}
    >
      {topRankLayout(prizeList.length, isMobile).map((rank) => {
        const index = rank - 1;
        const { label, prize } = prizeList[index];
        return (
          <li className={getTopRankItemStyle(rank)} key={rank}>
            <ImgOrLottie
              url={getIconUrl(rank)}
              alt={`rank${rank}icon`}
              css={{ w: rank > 1 ? 120 : 160, h: rank > 1 ? 120 : 160 }}
            />
            <div>
              <p
                className={css({
                  textStyle: 'Title/M',
                  color: 'content.default.level-5',
                  textAlign: 'center',
                })}
              >
                {label}
              </p>
              <p
                className={css({
                  textStyle: 'Title/S',
                  color: 'content.default.level-2',
                  textAlign: 'center',
                  pt: 8,
                })}
              >
                <FormattedMessage
                  id='Competition.r251215.Up_to_amount_symbol'
                  values={{
                    amountSymbol: (
                      <>
                        <Formatted.Number value={prize} css={{ color: 'var(--key-color)' }} /> USDT
                      </>
                    ),
                  }}
                />
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const MoreThan3RankReward = ({ prizeList }: RankRewardProps) => {
  return (
    <>
      <TopRankList prizeList={prizeList.slice(0, 3)} />
      <ul
        className={css({
          display: 'grid',
          gridTemplateColumns: { base: '1fr', tablet: 'repeat(2, minmax(0, 1fr))' },
          gap: { base: 12, tablet: 24 },
          mt: { base: 16, tablet: 24 },
          // 마지막 아이템이면서, 아이템 개수가 홀수일 때만 전체 폭으로 확장
          '& > li:last-child:nth-child(odd)': {
            gridColumn: { base: 'auto', tablet: '1 / -1' },
          },
        })}
      >
        {prizeList.slice(3).map(({ label, prize }) => (
          <li
            className={center({
              flexDir: 'column',
              gap: 8,
              py: 16,
              px: 8,
              rounded: 12,
              bg: 'background.default.level-1',
            })}
            key={prize}
          >
            <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-5', textAlign: 'center' })}>
              {label}
            </p>
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-2', textAlign: 'center' })}>
              <FormattedMessage
                id='Competition.r251215.Up_to_amount_symbol'
                values={{
                  amountSymbol: (
                    <Formatted.Number value={prize} precision={1} method='round-half-up' suffix={UsdtCurrency} />
                  ),
                }}
              />
            </p>
          </li>
        ))}
      </ul>
    </>
  );
};

const RankingRewardLayout = ({
  title,
  children,
  css: cssProp,
}: {
  title?: ReactNode;
  children: ReactNode;
  css?: Styles;
}) => {
  return (
    <section className={css(cssProp)}>
      {title && (
        <p
          className={css({
            textStyle: 'Title/S',
            color: 'content.default.level-5',
            textAlign: 'left',
            pb: 24,
          })}
        >
          {title}
        </p>
      )}
      {children}
    </section>
  );
};

export const RankingReward = ({ hasTitle }: { hasTitle?: boolean }) => {
  const { competitionAtom } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { competitionEventContentfulDataAtom } = useBunja(competitionEventsBunja);
  const competition = useAtomValue(competitionAtom);
  const competitionEventContentful = useAtomValue(competitionEventContentfulDataAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);

  if (!competitionEventContentful || !competition) return null;

  const maxRankPrizes = competition.maxRankPrizes[selectedEventTabValue as RankingType];
  if (!maxRankPrizes?.length) return null;

  const maxRankPrizesList = maxRankPrizes
    .sort((a, b) => a.rankFrom - b.rankFrom)
    .map(({ prize, rankFrom, rankTo }) => {
      if (rankFrom === rankTo)
        return {
          label: <FormattedMessage id='Competition.r251215.Top_rank' values={{ rank: rankFrom }} />,
          prize,
        };
      return {
        label: (
          <FormattedMessage id='Competition.r251215.Top_rank_range' values={{ rankRange: `${rankFrom} - ${rankTo}` }} />
        ),
        prize,
      };
    });
  const isZeroReward = Number(maxRankPrizes[0]?.prize) === 0;
  const isRoi = selectedEventTabValue === 'roi';
  const title =
    selectedEventTabValue === 'volume' ? (
      <FormattedMessage id='Competition.r251215.Volume_trading_rewards' />
    ) : isRoi ? (
      <FormattedMessage id='Tmp.inseok.Roi_trading_rewards' />
    ) : (
      <FormattedMessage id='Competition.r251218.Pnl_trading_reward' />
    );

  if (isZeroReward) return null;
  return (
    <RankingRewardLayout title={hasTitle ? title : undefined} css={!hasTitle ? { mb: 96 } : undefined}>
      {maxRankPrizesList.length <= 3 && <TopRankList prizeList={maxRankPrizesList} />}
      {maxRankPrizesList.length > 3 && <MoreThan3RankReward prizeList={maxRankPrizesList} />}
    </RankingRewardLayout>
  );
};
